import React, { useEffect, useState } from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, Sparkles, Heart, Activity, Users } from 'lucide-react';

const LoginTransition = ({ staffName, onComplete, todayTasks = [] }) => {
  const [phase, setPhase] = useState(0);
  const [show, setShow] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [canSkip, setCanSkip] = useState(false);
  
  // 時間帯に応じた挨拶を取得
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 10) return "おはようございます";
    if (hour < 17) return "こんにちは";
    return "お疲れ様です";
  };
  
  // 励ましメッセージをランダムに選択
  const getEncouragement = () => {
    const messages = [
      "今日も一日頑張りましょう",
      "素敵な一日になりますように",
      "今日もよろしくお願いします",
      "一緒に頑張りましょう",
      "今日も笑顔で過ごしましょう",
      "チーム一丸となって頑張りましょう",
      "患者さんのために最善を尽くしましょう",
      "今日も安全第一で行きましょう"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };
  
  // 曜日を取得
  const getDayOfWeek = () => {
    const days = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'];
    return days[currentTime.getDay()];
  };
  
  useEffect(() => {
    const timers = [];
    
    // フェーズ1: ローディングアニメーション
    timers.push(setTimeout(() => setPhase(1), 800));
    
    // フェーズ2: 挨拶メッセージ
    timers.push(setTimeout(() => setPhase(2), 2000));
    
    // フェーズ3: タスクリマインダー
    timers.push(setTimeout(() => setPhase(3), 3500));
    
    // 2秒後からスキップ可能に
    timers.push(setTimeout(() => setCanSkip(true), 2000));
    
    // フェーズ4: フェードアウト
    // タスクの数に応じて表示時間を調整
    const baseTime = 6000; // 基本6秒
    const additionalTime = Math.min(todayTasks.length * 500, 2000); // タスク1つにつき0.5秒追加（最大2秒）
    const totalTime = baseTime + additionalTime;
    
    timers.push(setTimeout(() => {
      setShow(false);
      setTimeout(() => onComplete(), 500);
    }, totalTime));
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [onComplete, todayTasks.length]);
  
  // スキップ処理
  const handleSkip = () => {
    if (canSkip) {
      setShow(false);
      setTimeout(() => onComplete(), 300);
    }
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 z-50 flex items-center justify-center overflow-hidden transition-opacity duration-500"
         style={{ opacity: show ? 1 : 0 }}>
      
      {/* スキップボタン */}
      {canSkip && (
        <button
          onClick={handleSkip}
          className="absolute top-6 right-6 px-4 py-2 bg-white/80 hover:bg-white text-slate-600 rounded-lg shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-105 text-sm font-medium flex items-center gap-2 z-20"
          style={{
            animation: 'fade-in-up 0.5s ease-out forwards',
          }}
        >
          スキップ
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      )}
      
      {/* 背景の幾何学的パターン */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
              <polygon points="24.8,22 37.3,29.2 37.3,43.7 24.8,50.9 12.3,43.7 12.3,29.2" 
                       fill="none" 
                       stroke="#1e40af" 
                       strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexagons)" />
        </svg>
      </div>
      
      {/* フローティングドット */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-blue-600"
            style={{
              width: `${4 + Math.random() * 4}px`,
              height: `${4 + Math.random() * 4}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.1 + Math.random() * 0.2,
              animation: `float ${15 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>
      
      {/* メインコンテンツ */}
      <div className="relative z-10 max-w-2xl mx-auto px-8 text-center">
        
        {/* フェーズ1: ローディングアニメーション */}
        {phase >= 0 && (
          <div className={`transition-all duration-1000 ${phase > 1 ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            <div className="relative w-32 h-32 mx-auto mb-8">
              {/* DNAヘリックス風のアニメーション */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-3 h-3 bg-blue-600 rounded-full"
                      style={{
                        left: '50%',
                        top: '50%',
                        transform: `rotate(${i * 45}deg) translateX(40px)`,
                        animation: `orbit 2s linear infinite`,
                        animationDelay: `${i * 0.1}s`,
                        opacity: 0.8
                      }}
                    />
                  ))}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-full shadow-2xl flex items-center justify-center">
                      <Activity className="w-8 h-8 text-blue-600 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-slate-700 text-xl font-light tracking-wider">
              システムに接続中
            </div>
            <div className="mt-2 flex justify-center gap-1">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.2}s` }}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* フェーズ2: 挨拶メッセージ */}
        {phase >= 2 && (
          <div className={`transition-all duration-1000 transform ${
            phase === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}>
            <div className="text-sm font-medium text-slate-500 mb-3 tracking-wider animate-fade-in-up">
              {currentTime.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })} {getDayOfWeek()}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 animate-fade-in-up animation-delay-200">
              {getGreeting()}
            </h1>
            
            <div className="flex items-center justify-center gap-3 mb-6 animate-fade-in-up animation-delay-400">
              <div className="h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent w-20" />
              <p className="text-2xl md:text-3xl font-medium text-blue-600">
                {staffName} さん
              </p>
              <div className="h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent w-20" />
            </div>
            
            <p className="text-lg text-slate-600 animate-fade-in-up animation-delay-600 flex items-center justify-center gap-3">
              <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-blue-600" />
              </span>
              {getEncouragement()}
              <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Heart className="w-4 h-4 text-blue-600" />
              </span>
            </p>
          </div>
        )}
        
        {/* フェーズ3: タスクリマインダー */}
        {phase >= 3 && todayTasks.length > 0 && (
          <div className="mt-12 transition-all duration-1000 transform opacity-0 translate-y-4 animate-fade-in-up animation-delay-800">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-blue-100">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center justify-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                本日の予定
              </h3>
              <div className="space-y-3">
                {todayTasks.slice(0, 3).map((task, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 text-left p-3 bg-blue-50 rounded-lg animate-fade-in-left"
                    style={{ animationDelay: `${1000 + index * 100}ms` }}
                  >
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                      {index + 1}
                    </div>
                    <span className="text-slate-700 text-sm leading-relaxed">{task}</span>
                  </div>
                ))}
              </div>
              {todayTasks.length > 3 && (
                <p className="mt-3 text-sm text-slate-500">
                  他 {todayTasks.length - 3} 件の予定があります
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* CSSアニメーション */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-left {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes orbit {
          from {
            transform: rotate(0deg) translateX(40px) rotate(0deg);
          }
          to {
            transform: rotate(360deg) translateX(40px) rotate(-360deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(10px) translateX(-10px);
          }
          75% {
            transform: translateY(-10px) translateX(20px);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .animate-fade-in-left {
          animation: fade-in-left 0.6s ease-out forwards;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        
        .animation-delay-600 {
          animation-delay: 600ms;
        }
        
        .animation-delay-800 {
          animation-delay: 800ms;
        }
      `}</style>
    </div>
  );
};

export default LoginTransition;